/**
 * LexicalMessageEditor — the APP-SIDE HOST for the lexical editor.
 *
 * ── What this is now ───────────────────────────────────────────────────────────
 * The actual editor implementation (lexical + @lexical/* + every node subclass,
 * plugin and the serializer, ~120KB+ min) moved into the lazy component bundle
 * `dist/lazy/lexical.js` (see LexicalEditorCore.tsx + src/extension/lazy-entries/
 * lexical-lazy.js). This file stays in app.js as a THIN host that:
 *   1. Reads the three app-graph couplings the editor needs
 *      (Redux dispatch, i18n placeholder, @mention candidates), and
 *   2. Renders the lazily-loaded <LexicalEditorCore> inside a <Suspense>
 *      boundary, forwarding the imperative ref through.
 *
 * The public API (default export, props, `LexicalMessageEditorRef`) is UNCHANGED,
 * so all consumers (message-editor-controlled, whatsapp-message,
 * team-inbox-message, preview-message-bubble, message-editor-message-box) need no
 * edits.
 *
 * ── Loading / fallback ─────────────────────────────────────────────────────────
 * `loadLexicalEditor()` (src/extension/utils/lazy-lib.ts) fetches the core from
 * the CSP-safe WAR bundle on WhatsApp Web; on any failure (or on the website,
 * which has no `chrome` runtime) it falls back to an eager webpack-bundled copy
 * while `__BT_LEXICAL_LAZY_FALLBACK__` is true. The scheduled-message dialog that
 * hosts the editor is already DeferUntilOpen-gated (R13-B), so the one-time
 * bundle fetch happens on first dialog open — ideal UX timing.
 */
import React, { forwardRef, Suspense, lazy, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/common/reducers/hooks/store.hook';
import { setActiveFormats } from '@common/reducers/ScheduledMessageEditorReducer';
import { loadLexicalEditor } from '@/extension/utils/lazy-lib';
import { useMentionCandidates } from './hooks/useMentionCandidates';
import type { LexicalMessageEditorRef, ActiveFormatsPayload, LexicalEditorCoreProps } from './LexicalEditorCore';

export type { LexicalMessageEditorRef } from './LexicalEditorCore';

interface LexicalMessageEditorProps {
  isPreview?: boolean;
  onFilesSelected?: (files: File[]) => void;
  content?: string; // Content is now the primary way to set value
  onChange?: (markdown: string) => void;
  /** FE#384 — stretch the editor to fill its parent height (opt-in). */
  fillHeight?: boolean;
}

/**
 * React.lazy over the lexical editor core. The resolved default is a forwardRef
 * component, so refs pass through the lazy boundary (React 18). Memoized once per
 * document by loadLexicalEditor().
 */
const LazyLexicalEditorCore = lazy(async () => {
  const { LexicalEditorCore } = await loadLexicalEditor();
  // loadLexicalEditor types the core as `any`; re-assert the forwardRef shape so
  // refs + props type-check through the lazy boundary.
  return {
    default: LexicalEditorCore as React.ForwardRefExoticComponent<
      LexicalEditorCoreProps & React.RefAttributes<LexicalMessageEditorRef>
    >,
  };
});

const LexicalMessageEditor = forwardRef<LexicalMessageEditorRef, LexicalMessageEditorProps>(
  (props, ref) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('common');
    // Same as before the split: the mention machinery runs for every editor
    // instance (it used to run inside the composer's MentionPlugin). We compute
    // the candidates here in the app graph and feed them into the lazy bundle
    // via props → MentionCandidatesContext.
    const { candidates, isLoading } = useMentionCandidates();

    const handleActiveFormatsChange = useCallback(
      (formats: ActiveFormatsPayload) => {
        dispatch(setActiveFormats(formats));
      },
      [dispatch],
    );

    return (
      <Suspense
        fallback={
          <div
            style={{
              width: '100%',
              // FE#384 fillHeight (opt-in): flex-fill for the tabbed dialog.
              // Otherwise prod's FE#385 CSS-var floor — same as the real editor
              // (LexicalEditorCore) so the lazy-load swap does not jump.
              ...(props.fillHeight
                ? { flex: '1 1 auto', minHeight: 120 }
                : { minHeight: props.isPreview ? undefined : 'var(--bt-editor-min-h, 170px)' }),
            }}
            aria-busy="true"
          />
        }
      >
        <LazyLexicalEditorCore
          ref={ref}
          isPreview={props.isPreview}
          onFilesSelected={props.onFilesSelected}
          content={props.content}
          onChange={props.onChange}
          placeholder={props.isPreview ? '' : t('chatView.typeYourMessage')}
          onActiveFormatsChange={handleActiveFormatsChange}
          mentionCandidates={candidates}
          mentionsLoading={isLoading}
          fillHeight={props.fillHeight}
        />
      </Suspense>
    );
  },
);

LexicalMessageEditor.displayName = 'LexicalMessageEditor';

export default LexicalMessageEditor;
